import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import TipTapEditor from '@/components/TipTapEditor';
import GameRequestPlatformPicker from '@/components/GameRequestPlatformPicker';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/toast';
import {
  GAME_REQUEST_VERSION_MAX_LENGTH,
  extractErrorMessage,
  getCsrfToken,
  getDefaultUpdateRequestDescription,
} from '@/utils/gameRequests';

interface GameUpdateRequestPanelProps {
  gameId: number;
  gameTitle: string;
  onCancel: () => void;
  onCreated: () => void;
}

const GameUpdateRequestPanel: React.FC<GameUpdateRequestPanelProps> = ({ gameId, gameTitle, onCancel, onCreated }) => {
  const { currentUser } = useAuth();
  const requesterName = currentUser?.translaterName || currentUser?.alias || currentUser?.name || '';

  const [platforms, setPlatforms] = useState<string[]>([]);
  const [usedPlatforms, setUsedPlatforms] = useState<string[]>([]);
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setDescription(getDefaultUpdateRequestDescription(requesterName));
  }, [requesterName]);

  useEffect(() => {
    fetch('/api/game_requests/platforms', { headers: { Accept: 'application/json' } })
      .then(response => (response.ok ? response.json() : []))
      .then(setUsedPlatforms)
      .catch(() => setUsedPlatforms([]));
  }, []);

  const handleSubmit = async () => {
    if (platforms.length === 0) {
      showError('Legalább egy platformot ki kell választani.');
      return;
    }
    if (!version.trim()) {
      showError('A verzió megadása kötelező.');
      return;
    }

    const formData = new FormData();
    formData.append('game_request[game_id]', String(gameId));
    platforms.forEach(platform => formData.append('game_request[platforms][]', platform));
    formData.append('game_request[version]', version.trim());
    formData.append('game_request[description]', description);

    setSubmitting(true);
    try {
      const response = await fetch('/api/game_requests', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        body: formData,
      });

      if (!response.ok) {
        showError(await extractErrorMessage(response, 'Hiba történt a kérés mentésekor.'));
        return;
      }

      showSuccess('A frissítés kérésed elmentve.');
      onCreated();
    } catch {
      showError('Hálózati hiba történt.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 rounded-md border border-border bg-gray-900/40 p-4">
      <div>
        <Label className="mb-1 block">Játék</Label>
        <p className="font-semibold">{gameTitle}</p>
      </div>

      <div>
        <Label className="mb-2 block">Platform</Label>
        <GameRequestPlatformPicker selected={platforms} onChange={setPlatforms} usedPlatforms={usedPlatforms} />
      </div>

      <div>
        <Label htmlFor="update-request-version" className="mb-2 block">Verzió</Label>
        <Input
          id="update-request-version"
          value={version}
          maxLength={GAME_REQUEST_VERSION_MAX_LENGTH}
          onChange={e => setVersion(e.target.value)}
          placeholder="pl. 1.2.0"
        />
      </div>

      <div>
        <Label className="mb-2 block">Leírás</Label>
        <TipTapEditor
          value={description}
          onChange={setDescription}
          placeholder="Írd le, mi változott az új verzióban..."
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Mégse
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Küldés...' : 'Kérés küldése'}
        </Button>
      </div>
    </div>
  );
};

export default GameUpdateRequestPanel;
